import { Controller } from "@hotwired/stimulus"

// Drives the dashboard's single global filter and auto-refresh (see
// custom-dashboard-widgets design.md Decision 4): on period-select change or
// on a refresh-timer tick, it walks every widget turbo-frame on the grid and
// rewrites its `src` query string's `period` param. Turbo's native
// behavior (a `src` change triggers an automatic refetch of that frame)
// does the rest — each widget still refetches independently, same as the
// initial page load, rather than one coordinated multi-target response.
export default class extends Controller {
  static targets = ["period", "refresh"]
  static values = { period: String, saveUrl: String }

  connect() {
    this.timer = null
    this.scheduleRefresh()
  }

  disconnect() {
    this.clearTimer()
  }

  periodChanged() {
    this.periodValue = this.periodTarget.value
    this.refreshFrames()
  }

  // Persists immediately on change — unlike the widget grid (batched behind
  // an explicit edit-mode Save), this select is visible and usable outside
  // edit mode, so there's no separate "Save" action to hang the write on.
  refreshChanged() {
    this.scheduleRefresh()
    this.saveRefreshInterval()
  }

  async saveRefreshInterval() {
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content

    try {
      await fetch(this.saveUrlValue, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          "X-CSRF-Token": csrfToken || ""
        },
        credentials: "same-origin",
        body: JSON.stringify({ refresh_interval: this.refreshTarget.value })
      })
    } catch (error) {
      // Best-effort: a failed save just means the interval reverts to the
      // last-saved value on next page load — not worth interrupting the
      // user with an alert over, unlike a lost widget layout edit.
    }
  }

  refreshFrames() {
    this.element.querySelectorAll('turbo-frame[id^="widget_"]').forEach((frame) => {
      if (!frame.src) return

      const url = new URL(frame.src, window.location.href)
      url.searchParams.set("period", this.periodValue)
      frame.src = url.toString()
    })
  }

  scheduleRefresh() {
    this.clearTimer()
    const seconds = parseInt(this.refreshTarget.value, 10)
    if (!seconds) return

    this.timer = setInterval(() => this.refreshFrames(), seconds * 1000)
  }

  clearTimer() {
    if (this.timer) {
      clearInterval(this.timer)
      this.timer = null
    }
  }
}
